<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DateTime } from 'luxon';

export default {
  components: {
    FontAwesomeIcon,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      versions: [],
      loading: false,
      showVersions: false,
    };
  },
  watch: {
    'quote.updated_at'() {
      // Reload versions when the quote is updated
      this.loadVersions();
    },
  },
  mounted() {
    this.loadVersions();
  },
  methods: {
    async loadVersions() {
      if (this.loading) return;
      
      this.loading = true;
      try {
        const response = await this.$http.get(`admin/quotes/${this.quote.id}/versions`);
        this.versions = response.data.versions;
      } catch (error) {
        console.error('Failed to load versions:', error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      try {
        return DateTime.fromISO(date).toFormat('dd.MM.yyyy HH:mm');
      } catch (e) {
        return DateTime.fromSQL(date).toFormat('dd.MM.yyyy HH:mm');
      }
    },
    viewVersion(versionId) {
      // Open version in new tab
      const route = this.$router.resolve({ name: 'quotes.edit', params: { id: versionId } });
      window.open(route.href, '_blank');
    },
    toggleVersions() {
      this.showVersions = !this.showVersions;
    },
  },
};
</script>

<template>
  <div v-if="quote.parent_quote_id !== null || versions.length > 1">
    <!-- Banner when viewing an old version -->
    <b-alert v-if="quote.parent_quote_id !== null" show variant="warning" class="mb-3">
      <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" />
      {{ $t('messages.viewing_old_version') }} v{{ quote.version_number }}
      <b-button 
        variant="warning" 
        size="sm" 
        class="ml-2"
        @click="viewVersion(quote.parent_quote_id)"
      >
        <FontAwesomeIcon :icon="['fas', 'arrow-right']" />
        {{ $t('actions.view_current_version') }}
      </b-button>
    </b-alert>

    <!-- Version history list -->
    <div v-else-if="versions.length > 1" class="version-history mb-3">
      <b-button 
        variant="outline-secondary" 
        size="sm" 
        @click="toggleVersions"
      >
        <FontAwesomeIcon :icon="['fas', 'history']" />
        {{ $t('titles.version_history') }} ({{ versions.length }})
        <FontAwesomeIcon :icon="['fas', showVersions ? 'arrow-up' : 'arrow-down']" class="ml-1" />
      </b-button>

      <b-collapse v-model="showVersions" class="mt-2">
      <b-card>
        <b-table
          :items="versions"
          :fields="[
            { key: 'version_number', label: $t('fields.version'), sortable: false },
            { key: 'versioned_at', label: $t('fields.created_at'), sortable: false },
            { key: 'updated_by', label: $t('fields.updated_by'), sortable: false },
            { key: 'version_notes', label: $t('fields.notes'), sortable: false },
            { key: 'is_latest_version', label: $t('fields.current'), sortable: false },
            { key: 'actions', label: $t('table_labels.actions'), sortable: false },
          ]"
          small
          striped
          hover
          responsive
        >
          <template #cell(version_number)="row">
            <b-badge :variant="row.item.is_latest_version ? 'success' : 'secondary'">
              v{{ row.item.version_number }}
            </b-badge>
          </template>

          <template #cell(versioned_at)="row">
            {{ formatDate(row.item.versioned_at || row.item.updated_at) }}
          </template>

          <template #cell(updated_by)="row">
            <small v-if="row.item.updated_by">
              {{ row.item.updated_by.first_name }} {{ row.item.updated_by.last_name }}
            </small>
            <small v-else class="text-muted">-</small>
          </template>

          <template #cell(version_notes)="row">
            <small class="text-muted">{{ row.item.version_notes || '-' }}</small>
          </template>

          <template #cell(is_latest_version)="row">
            <b-badge v-if="row.item.is_latest_version" variant="success">
              <FontAwesomeIcon :icon="['fas', 'check']" />
              {{ $t('titles.current_version') }}
            </b-badge>
          </template>

          <template #cell(actions)="row">
            <b-button
              v-if="row.item.id !== quote.id"
              variant="outline-primary"
              size="sm"
              @click="viewVersion(row.item.id)"
            >
              <FontAwesomeIcon :icon="['fas', 'eye']" />
              {{ $t('actions.view') }}
            </b-button>
            <b-badge v-else variant="info" class="p-2">
              {{ $t('titles.current_page') }}
            </b-badge>
          </template>
        </b-table>
      </b-card>
    </b-collapse>
  </div>
  </div>
</template>

<style scoped>
.version-history {
}
</style>
